/**
 * Translating what a page shows, in the browser: which languages Helm offers,
 * whether a text is worth a "Translate", and a client that gathers what the
 * page's blocks ask for into a few requests and remembers the answers.
 *
 * Kept free of React and of Helm's path aliases so the API's tests can import
 * it. `detectLanguage` is the same rule as the API's (src/translation/
 * language.ts); a test keeps the two in step.
 */

export const TRANSLATION_TARGETS = ['en', 'zh', 'fr'] as const
export type TranslationTarget = (typeof TRANSLATION_TARGETS)[number]

export type TranslationContext =
  'courier_scan' | 'item' | 'message' | 'note' | 'supplier_quote' | 'activity'

/** The language picker, each language in its own words. */
export const TARGET_LABELS: Record<TranslationTarget, string> = {
  en: 'English',
  zh: '中文',
  fr: 'Français',
}

const LANGUAGE_NAMES: Record<string, string> = {
  zh: 'Chinese',
  en: 'English',
  fr: 'French',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian',
  ar: 'Arabic',
  es: 'Spanish',
  pt: 'Portuguese',
  sw: 'Swahili',
}

/** "Chinese" for "zh": what "Translated from …" says. */
export function languageName(code: string | null | undefined): string | null {
  if (!code) return null
  if (LANGUAGE_NAMES[code]) return LANGUAGE_NAMES[code]!
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? null
  } catch {
    return null
  }
}

// Limits the API keeps (src/routes/translate.ts); a request is kept well inside them.
export const MAX_TEXT_CHARS = 5_000
const REQUEST_TEXTS = 25
const REQUEST_CHARS = 4_000

const FRENCH_WORDS = new Set(
  'le la les de des du un une et est sont pour avec dans sur au aux que qui pas ne nous vous je il elle ce cette ces été être avez avons votre vos notre nos merci bonjour colis livraison livré commande'.split(
    ' ',
  ),
)
const ENGLISH_WORDS = new Set(
  'the and of to in is are was were for with on at by from this that it we you your our please not be has have will can been'.split(
    ' ',
  ),
)
const LINK = /@|^(https?:\/\/|www\.)/i
const LATIN_RUN = /[\p{Script=Latin}\p{N}_-]+/gu

function wordsOf(text: string) {
  return text
    .split(/\s+/)
    .filter((token) => !LINK.test(token))
    .map((token) => token.replace(LATIN_RUN, (run) => (/\p{N}/u.test(run) ? ' ' : run)))
    .join(' ')
}

/** A text's language as its letters tell: "other" for another script; null with no words. */
export function detectLanguage(text: string): TranslationTarget | 'other' | null {
  const words = wordsOf(text)
  const han = words.match(/\p{Script=Han}/gu)?.length ?? 0
  const latin = words.match(/\p{Script=Latin}/gu)?.length ?? 0
  const letters = words.match(/\p{L}/gu)?.length ?? 0
  const other = letters - han - latin
  if (letters === 0) return null
  if (han * 3 >= latin && han * 3 >= other) return 'zh'
  if (other > latin) return 'other'
  const latinWords = words.toLowerCase().match(/\p{Script=Latin}+/gu) ?? []
  let french = (words.match(/[àâçéèêëîïôûùüÿœæ]/giu)?.length ?? 0) > 0 ? 2 : 0
  let english = 0
  for (const word of latinWords) {
    if (FRENCH_WORDS.has(word)) french += 1
    if (ENGLISH_WORDS.has(word)) english += 1
  }
  return french > english ? 'fr' : 'en'
}

/** Whether to offer "Translate" for a text: words, in another language, not too long. */
export function offersTranslation(text: string | null | undefined, target: TranslationTarget) {
  if (!text || text.length > MAX_TEXT_CHARS) return false
  const language = detectLanguage(text)
  return language !== null && language !== target
}

export interface TranslationResult {
  text: string
  sourceLanguage: string | null
  translated: boolean
}

/** Sends one request: texts in, translations in the same order. */
export type SendTranslations = (
  texts: string[],
  target: TranslationTarget,
  context: TranslationContext,
) => Promise<TranslationResult[]>

interface Waiting {
  text: string
  resolve: (result: TranslationResult) => void
  reject: (error: unknown) => void
}

const keyOf = (text: string, target: TranslationTarget) => `${target}\u0000${text}`

/**
 * Gathers what a page's blocks ask for over a moment into as few requests as
 * the limits allow, one text asked once however many blocks show it, and
 * keeps every answer for the rest of the visit. Blocks follow it through
 * `subscribe`: each answer, or failure, bumps `version`.
 */
export class TranslationClient {
  private results = new Map<string, TranslationResult>()
  private failures = new Map<string, string>()
  private inFlight = new Map<string, Promise<TranslationResult>>()
  private queue = new Map<string, Map<string, Waiting[]>>()
  private timer: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<() => void>()
  private revision = 0

  constructor(
    private readonly send: SendTranslations,
    private readonly delayMs = 30,
  ) {}

  /** A translation already known, for showing it without waiting. */
  peek(text: string, target: TranslationTarget): TranslationResult | undefined {
    return this.results.get(keyOf(text, target))
  }

  /** Why the last try for a text failed, until it is tried again. */
  failure(text: string, target: TranslationTarget): string | undefined {
    return this.failures.get(keyOf(text, target))
  }

  pending(text: string, target: TranslationTarget): boolean {
    return this.inFlight.has(keyOf(text, target))
  }

  /** Clears the failures of these texts, so they are asked for again. */
  retry(texts: string[], target: TranslationTarget) {
    let changed = false
    for (const text of texts) changed = this.failures.delete(keyOf(text, target)) || changed
    if (changed) this.notify()
  }

  translate(
    texts: string[],
    target: TranslationTarget,
    context: TranslationContext,
  ): Promise<TranslationResult[]> {
    return Promise.all(
      texts.map((text) => {
        const key = keyOf(text, target)
        const known = this.results.get(key)
        if (known) return Promise.resolve(known)
        const flying = this.inFlight.get(key)
        if (flying) return flying
        const promise = new Promise<TranslationResult>((resolve, reject) => {
          const group = `${target}\u0000${context}`
          const waiting = this.queue.get(group) ?? new Map<string, Waiting[]>()
          this.queue.set(group, waiting)
          waiting.set(text, [...(waiting.get(text) ?? []), { text, resolve, reject }])
        })
        this.inFlight.set(key, promise)
        this.schedule()
        return promise
      }),
    )
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  version = () => this.revision

  private notify() {
    this.revision += 1
    for (const listener of this.listeners) listener()
  }

  private schedule() {
    if (this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, this.delayMs)
  }

  private async flush() {
    const groups = [...this.queue]
    this.queue.clear()
    await Promise.all(
      groups.flatMap(([group, waiting]) => {
        const [target, context] = group.split('\u0000') as [TranslationTarget, TranslationContext]
        return chunk([...waiting.keys()]).map((texts) =>
          this.request(texts, target, context, waiting),
        )
      }),
    )
  }

  private async request(
    texts: string[],
    target: TranslationTarget,
    context: TranslationContext,
    waiting: Map<string, Waiting[]>,
  ) {
    try {
      const results = await this.send(texts, target, context)
      texts.forEach((text, index) => {
        const key = keyOf(text, target)
        const result = results[index] ?? { text, sourceLanguage: null, translated: false }
        this.results.set(key, result)
        this.failures.delete(key)
        this.inFlight.delete(key)
        for (const entry of waiting.get(text) ?? []) entry.resolve(result)
      })
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Translation failed'
      for (const text of texts) {
        const key = keyOf(text, target)
        this.failures.set(key, message)
        this.inFlight.delete(key)
        for (const entry of waiting.get(text) ?? []) entry.reject(error)
      }
    }
    this.notify()
  }
}

/** Texts in requests the API answers quickly: a few dozen short ones, or fewer long ones. */
export function chunk(texts: string[]): string[][] {
  const chunks: string[][] = []
  let current: string[] = []
  let chars = 0
  for (const text of texts) {
    if (
      current.length > 0 &&
      (current.length >= REQUEST_TEXTS || chars + text.length > REQUEST_CHARS)
    ) {
      chunks.push(current)
      current = []
      chars = 0
    }
    current.push(text)
    chars += text.length
  }
  if (current.length > 0) chunks.push(current)
  return chunks
}

/** What a person chose: whether the whole page is translated, and into what. Kept per person. */
export interface TranslationPreference {
  page: boolean
  target: TranslationTarget
}

export const DEFAULT_PREFERENCE: TranslationPreference = { page: false, target: 'en' }

export const preferenceKey = (userId: string) => `helm:translation:${userId}`

/** Reads a stored preference; anything malformed falls back to the default. */
export function parsePreference(raw: string | null | undefined): TranslationPreference {
  try {
    const value = JSON.parse(raw ?? 'null') as Partial<TranslationPreference> | null
    return {
      page: value?.page === true,
      target: TRANSLATION_TARGETS.includes(value?.target as TranslationTarget)
        ? (value!.target as TranslationTarget)
        : DEFAULT_PREFERENCE.target,
    }
  } catch {
    return DEFAULT_PREFERENCE
  }
}

/** A block's own choice, made while the page was on or off; it counts only until the page is switched. */
export interface BlockChoice {
  on: boolean
  page: boolean
}

/** Whether a block shows its translation: its own choice if made under this page setting, else the page's. */
export function wantsTranslation(choice: BlockChoice | null, pageOn: boolean) {
  return choice && choice.page === pageOn ? choice.on : pageOn
}

/** The language most of a block's translated texts came from. */
export function dominantSource(results: Array<TranslationResult | undefined>): string | null {
  const counts = new Map<string, number>()
  for (const result of results) {
    if (!result?.translated || !result.sourceLanguage) continue
    counts.set(result.sourceLanguage, (counts.get(result.sourceLanguage) ?? 0) + 1)
  }
  let best: string | null = null
  for (const [language, count] of counts) {
    if (best === null || count > counts.get(best)!) best = language
  }
  return best
}
